export type ChangelogEntry = {
  readonly id: string;
  readonly date: string;
  readonly category: "UI" | "Scoring" | "MCP";
  readonly title: string;
  readonly description: string;
  readonly href: string;
  readonly linkLabel: string;
};

export const CHANGELOG_ENTRIES: readonly ChangelogEntry[] = [
  {
    id: "2026-11-04-scoring-filter-reliability",
    date: "2026-11-04",
    category: "Scoring",
    title: "More reliable score filters and faster growth searches",
    description:
      "Filters created with the new scoring engine can now be edited, activated and deleted reliably. Revenue-growth filters load faster while preserving their selection and scoring rules.",
    href: "/score-filter",
    linkLabel: "Open score filters",
  },
  {
    id: "2026-11-03-financial-report-details",
    date: "2026-11-03",
    category: "Scoring",
    title: "Filter by fiscal year details and reported employees",
    description:
      "Realtime score filters can use the length and ending month of a company's selected fiscal year, and the employee count reported in its annual report. Values retain the statement's source and reporting period.",
    href: "/score-filter",
    linkLabel: "Open score filters",
  },
  {
    id: "2026-11-03-mapped-news-count",
    date: "2026-11-03",
    category: "Scoring",
    title: "Score companies by recent news mentions",
    description:
      "Realtime score filters can use the number of company-mapped news articles from the last 14 days. The count covers monitored outlets; companies without matching article evidence have no value.",
    href: "/score-filter",
    linkLabel: "Open score filters",
  },
  {
    id: "2026-11-03-exact-organization-number",
    date: "2026-11-03",
    category: "Scoring",
    title: "Filter by exact organization number",
    description:
      "Realtime score filters can include or exclude exact organization numbers and lists of identifiers. Use the country-prefixed identifier shown on the company record.",
    href: "/score-filter",
    linkLabel: "Open score filters",
  },
  {
    id: "2026-11-03-follow-any-company",
    date: "2026-11-03",
    category: "UI",
    title: "Follow job ads for any company you choose",
    description:
      "Career page collection on a company's Hiring tab, and set_company_career_scraping in MCP, can now be enabled for any company with a verified domain, whatever its size, country or status. Only a domain shared by more than 40 companies stays blocked, because its job ads cannot be attributed.",
    href: "/landing",
    linkLabel: "Open Bloodline",
  },
  {
    id: "2026-11-02-company-domain-links",
    date: "2026-11-02",
    category: "UI",
    title: "Verify, add and remove company domains",
    description:
      "The career page collection card on a company's Hiring tab can now verify a linked domain, add a new one after checking who else shares it, and unverify or remove a wrong link. Every change applies to all users and is recorded with who made it.",
    href: "/landing",
    linkLabel: "Open Bloodline",
  },
  {
    id: "2026-11-02-career-domain-activation-mcp",
    date: "2026-11-02",
    category: "MCP",
    title: "Manage career collection through MCP",
    description:
      "Use get_company_career_scraping to inspect a company's domains and collection settings, then set_company_career_scraping to enable or disable a verified domain. Changes affect everyone using the shared domain; work already in progress may finish.",
    href: "/admin/claude-mcp",
    linkLabel: "View MCP setup instructions",
  },
  {
    id: "2026-11-02-career-domain-activation",
    date: "2026-11-02",
    category: "UI",
    title: "Choose which companies to monitor for job ads",
    description:
      "Use the Hiring tab on a company page to enable or disable career page collection for its verified domains. Domain settings apply to everyone, including other companies linked to the same domain. Work already in progress may finish.",
    href: "/landing",
    linkLabel: "Open Bloodline",
  },
  {
    id: "2026-10-30-financial-report-metadata",
    date: "2026-10-30",
    category: "Scoring",
    title: "Filter by financial reporting period",
    description:
      "Realtime scoring can filter companies by the fiscal year and currency of their selected financial statement, and by when Bloodline last observed those financials. Values carry the selected statement's source evidence.",
    href: "/score-filter",
    linkLabel: "Open score filters",
  },
  {
    id: "2026-10-30-direct-parent-company-metric",
    date: "2026-10-30",
    category: "Scoring",
    title: "Filter by direct parent company",
    description:
      "Realtime scoring can filter by a company's immediate Valu8 parent company name. When a company has multiple direct parents, the relation with the highest reported ownership stake is selected; sparse values retain their source evidence.",
    href: "/score-filter",
    linkLabel: "Open score filters",
  },
  {
    id: "2026-10-25-hiring-name-resolution",
    date: "2026-10-25",
    category: "MCP",
    title: "Find hiring evidence by company name",
    description:
      "Use research_hiring in Bloodline MCP with company names instead of manual company IDs. Legal-name variants and stored domain associations help identify companies; optional country hints narrow the search while ambiguous employers remain visible as candidates. Career and JobTech evidence retain their own dates, statuses, keywords, and attribution warnings.",
    href: "/admin/claude-mcp",
    linkLabel: "View MCP setup instructions",
  },
  {
    id: "2026-10-23-research-hiring",
    date: "2026-10-23",
    category: "MCP",
    title: "Research hiring across a company list",
    description:
      "Use research_hiring in Bloodline MCP to compare stored career-page and JobTech ads in one batch. Results include observation dates, ad status, keyword evidence, and explicit company, group, and employer-match limitations.",
    href: "/admin/claude-mcp",
    linkLabel: "View MCP setup instructions",
  },
  {
    id: "2026-10-22-hiring-observed-ads",
    date: "2026-10-22",
    category: "UI",
    title: "Compare hiring keywords across the same observed ads",
    description:
      "Hiring trends now includes observed ads from incomplete collections and uses the same current ad evidence as company Hiring. Coverage warnings remain visible, keyword shares use analyzed ads, and changes over time still require comparable history.",
    href: "/hiring-trends",
    linkLabel: "Explore hiring trends",
  },
  {
    id: "2026-10-21-career-domain-evidence",
    date: "2026-10-21",
    category: "MCP",
    title: "Inspect career ads with explicit company-match warnings",
    description:
      "Use get_careers_by_domains in Bloodline MCP to inspect stored ads and keywords even when a company-domain match is unverified or ambiguous. Results keep candidates visible and explain the uncertainty, including when a verified group association does not establish each ad's legal employer. Connected clients can now submit full domains, hiring filters, and ad IDs.",
    href: "/admin/claude-mcp",
    linkLabel: "View MCP setup instructions",
  },
  {
    id: "2026-10-21-hiring-trends",
    date: "2026-10-21",
    category: "UI",
    title: "Explore hiring demand across companies",
    description:
      "Find popular hiring keywords in Lead Views → Hiring trends. Filter stored career-page observations by industry and comparison period, then inspect the contributing companies and matching ads. Coverage and observation dates show the available evidence; changes appear only when comparable history exists.",
    href: "/hiring-trends",
    linkLabel: "Open hiring trends",
  },
  {
    id: "2026-10-21-hiring-keywords",
    date: "2026-10-21",
    category: "UI",
    title: "See every hiring keyword",
    description:
      "The Hiring view now gives every detected keyword its own tile, including keywords found in a single job ad. Search, ad counts, and the full keyword list remain available alongside the chart.",
    href: "/",
    linkLabel: "Search companies",
  },
  {
    id: "2026-10-20-parent-company-metrics",
    date: "2026-10-20",
    category: "Scoring",
    title: "Filter by ultimate parent company",
    description:
      "Realtime scoring can use Valu8's ultimate-parent name and organisation number. Values are available when Bloodline has field-level source evidence, so filters do not rely on unverified parent data.",
    href: "/score-filter",
    linkLabel: "Open score filters",
  },
  {
    id: "2026-10-19-job-ad-mcp-tools",
    date: "2026-10-19",
    category: "MCP",
    title: "Browse job ads and check their status with MCP",
    description:
      "MCP clients can page through matching company job ads and check known ad IDs for an explicit open, closed, expired, or unknown status. Stale evidence stays unknown, so absence from search is not treated as proof that an ad closed.",
    href: "/admin/claude-mcp",
    linkLabel: "View MCP setup instructions",
  },
  {
    id: "2026-10-08-company-group-structure",
    date: "2026-10-08",
    category: "UI",
    title: "Explore a company's ownership structure",
    description:
      "The Group Structure view shows the latest available company group as an ownership graph. Search for group members and inspect reported ownership stakes, including additional and cross-linked relationships.",
    href: "/company/SE5592282270#group-structure",
    linkLabel: "Open Eidra's Group Structure",
  },
];
